import type { JsonValue } from './json.types'
import { recordedTime, NOT_RECORDED } from '@/utils/recordedValues'

/**
 * Session and Audit Log Types
 * Matches: security.sessions, security.audit_log tables
 */

// ============================================================================
// SESSION TYPES
// ============================================================================

/**
 * Session Status
 */
export type SessionStatus = 'active' | 'expired' | 'revoked' | 'inactive' | 'unknown'

/**
 * Device Type
 */
export type DeviceType = 'desktop' | 'mobile' | 'tablet' | 'unknown'

/**
 * User Session
 * Matches: security.sessions table
 */
export interface UserSession {
  sessionId: string
  userId: string

  // Session token (hashed in real impl)
  sessionToken: string

  // Device info
  ipAddress?: string
  userAgent?: string
  deviceType: DeviceType
  deviceName?: string
  browser?: string
  browserVersion?: string
  os?: string
  osVersion?: string

  // Location (from IP)
  country?: string
  countryCode?: string
  city?: string
  region?: string

  // Status
  status: SessionStatus
  isCurrentSession?: boolean

  // Timestamps
  createdAt: string | null
  lastActivityAt: string | null
  expiresAt: string
  loggedOutAt?: string
}

/**
 * Session alias for backwards compatibility
 */
export type Session = UserSession

/**
 * Session Filters
 */
export interface SessionFilters {
  status?: SessionStatus | 'all'
  deviceType?: DeviceType | 'all'
  userId?: string
}

/**
 * Default session filters
 */
export const DEFAULT_SESSION_FILTERS: SessionFilters = {
  status: 'all',
  deviceType: 'all',
}

// ============================================================================
// AUDIT LOG TYPES
// ============================================================================

/**
 * Audit Actions
 */
export type AuditAction =
  // Authentication
  | 'login'
  | 'logout'
  | 'login_failed'
  | 'password_reset_requested'
  | 'password_reset_completed'
  | 'password_changed'
  | 'password_reset' // Legacy alias
  // User management
  | 'user_created'
  | 'user_updated'
  | 'user_deleted'
  | 'user_activated'
  | 'user_deactivated'
  | 'user_locked'
  | 'user_unlocked'
  | 'user_invited' // Legacy alias for invitation_sent
  // Role management
  | 'role_assigned'
  | 'role_revoked'
  // Invitation
  | 'invitation_sent'
  | 'invitation_accepted'
  | 'invitation_cancelled'
  | 'invitation_expired'
  // Session
  | 'session_created'
  | 'session_terminated'
  | 'all_sessions_terminated'
  // Settings
  | 'settings_updated'
  | 'tenant_updated'
  | 'subscription_changed'
  // Security
  | 'mfa_enabled'
  | 'mfa_disabled'
  | 'api_key_created'
  | 'api_key_revoked'

/**
 * Resource Types for audit
 */
export type AuditResourceType =
  | 'user'
  | 'role'
  | 'session'
  | 'invitation'
  | 'tenant'
  | 'subscription'
  | 'settings'
  | 'api_key'

/**
 * Audit Log Entry
 * Matches: security.audit_log table
 */
export interface AuditLogEntry {
  id: string

  // Who performed the action
  userId?: string
  userEmail?: string
  performedByName?: string

  // What action was performed
  action: AuditAction
  resourceType?: AuditResourceType
  resourceId?: string
  resourceName?: string

  // Action details (optional for simple events)
  details?: JsonValue

  // Request context
  ipAddress?: string
  userAgent?: string
  country?: string
  city?: string

  // Result
  success: boolean | null
  errorMessage?: string

  // When
  createdAt: string | null
}

/**
 * Audit Details - varies by action type
 */
export interface AuditDetails {
  // For user actions
  targetUserId?: string
  targetUserEmail?: string
  targetUserName?: string
  terminatedUserId?: string

  // For role changes
  oldRole?: string
  newRole?: string

  // For settings changes
  changedFields?: string[]
  oldValues?: Record<string, unknown>
  newValues?: Record<string, unknown>

  // For login / auth
  loginMethod?: string
  deviceInfo?: string
  attemptedEmail?: string
  email?: string
  method?: string
  tenantId?: string
  tokenUsed?: boolean

  // For session
  sessionId?: string
  terminatedSessionCount?: number

  // Generic
  reason?: string
  description?: string
  message?: string
  metadata?: Record<string, unknown>
}

/**
 * Audit Log Filters
 */
export interface AuditLogFilters {
  userId?: string
  action?: AuditAction | 'all'
  resourceType?: AuditResourceType | 'all'
  success?: boolean | 'all'
  dateFrom?: string
  dateTo?: string
  searchQuery?: string
}

/**
 * Default audit log filters
 */
export const DEFAULT_AUDIT_LOG_FILTERS: AuditLogFilters = {
  action: 'all',
  resourceType: 'all',
  success: 'all',
  searchQuery: '',
}

// ============================================================================
// REFRESH TOKEN TYPES
// ============================================================================

/**
 * Refresh Token
 * Matches: security.refresh_tokens table
 */
export interface RefreshToken {
  tokenId: string
  userId: string
  tokenHash: string
  isRevoked: boolean
  revokedAt?: string
  replacedById?: string
  createdAt: string
  expiresAt: string
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get session status display name
 */
export function getSessionStatusDisplayName(status: SessionStatus): string {
  switch (status) {
    case 'active':
      return 'Active'
    case 'expired':
      return 'Expired'
    case 'revoked':
      return 'Terminated'
    case 'inactive':
      return 'Inactive'
    case 'unknown':
      return NOT_RECORDED
    default:
      return 'Unknown'
  }
}

/**
 * Get session status color
 */
export function getSessionStatusColor(status: SessionStatus): string {
  switch (status) {
    case 'active':
      return 'success'
    case 'expired':
      return 'warning'
    case 'revoked':
      return 'error'
    default:
      return 'default'
  }
}

/**
 * Get device type icon name (for lucide-react)
 */
export function getDeviceTypeIcon(deviceType: DeviceType): string {
  switch (deviceType) {
    case 'desktop':
      return 'Monitor'
    case 'mobile':
      return 'Smartphone'
    case 'tablet':
      return 'Tablet'
    default:
      return 'HelpCircle'
  }
}

/**
 * Get audit action display name
 */
export function getAuditActionDisplayName(action: AuditAction): string {
  const actionNames: Record<AuditAction, string> = {
    login: 'Login',
    logout: 'Logout',
    login_failed: 'Failed Login',
    password_reset_requested: 'Password Reset Requested',
    password_reset_completed: 'Password Reset Completed',
    password_changed: 'Password Changed',
    password_reset: 'Password Reset',
    user_created: 'User Created',
    user_updated: 'User Updated',
    user_deleted: 'User Deleted',
    user_activated: 'User Activated',
    user_deactivated: 'User Deactivated',
    user_locked: 'User Locked',
    user_unlocked: 'User Unlocked',
    user_invited: 'User Invited',
    role_assigned: 'Role Assigned',
    role_revoked: 'Role Revoked',
    invitation_sent: 'Invitation Sent',
    invitation_accepted: 'Invitation Accepted',
    invitation_cancelled: 'Invitation Cancelled',
    invitation_expired: 'Invitation Expired',
    session_created: 'Session Created',
    session_terminated: 'Session Terminated',
    all_sessions_terminated: 'All Sessions Terminated',
    settings_updated: 'Settings Updated',
    tenant_updated: 'Tenant Updated',
    subscription_changed: 'Subscription Changed',
    mfa_enabled: 'MFA Enabled',
    mfa_disabled: 'MFA Disabled',
    api_key_created: 'API Key Created',
    api_key_revoked: 'API Key Revoked',
  }
  return actionNames[action] || action
}

/**
 * Get audit action category
 */
export function getAuditActionCategory(action: AuditAction): string {
  if (
    [
      'login',
      'logout',
      'login_failed',
      'password_reset_requested',
      'password_reset_completed',
      'password_changed',
    ].includes(action)
  ) {
    return 'Authentication'
  }
  if (action.startsWith('user_')) {
    return 'User Management'
  }
  if (action.startsWith('role_')) {
    return 'Role Management'
  }
  if (action.startsWith('invitation_')) {
    return 'Invitations'
  }
  if (action.startsWith('session_') || action === 'all_sessions_terminated') {
    return 'Sessions'
  }
  if (['settings_updated', 'tenant_updated', 'subscription_changed'].includes(action)) {
    return 'Settings'
  }
  if (['mfa_enabled', 'mfa_disabled', 'api_key_created', 'api_key_revoked'].includes(action)) {
    return 'Security'
  }
  return 'Other'
}

/**
 * Get audit action icon name (for lucide-react)
 */
export function getAuditActionIcon(action: AuditAction): string {
  const category = getAuditActionCategory(action)
  switch (category) {
    case 'Authentication':
      return 'LogIn'
    case 'User Management':
      return 'User'
    case 'Role Management':
      return 'Shield'
    case 'Invitations':
      return 'Mail'
    case 'Sessions':
      return 'Monitor'
    case 'Settings':
      return 'Settings'
    case 'Security':
      return 'Lock'
    default:
      return 'Activity'
  }
}

/**
 * Get success/failure badge color
 */
export function getAuditResultColor(success: boolean | null | undefined): string {
  return success === true ? 'success' : success === false ? 'error' : 'default'
}

/**
 * Parse user agent to get browser info
 */
export function parseUserAgent(userAgent: string): {
  browser: string
  browserVersion: string
  os: string
  osVersion: string
  deviceType: DeviceType
} {
  // Simplified parsing - in production, use a library like ua-parser-js
  let browser = 'API client'
  let browserVersion = ''
  let os = 'Not reported'
  let osVersion = ''
  let deviceType: DeviceType = 'unknown'

  // Browser detection
  if (/Edg\//.test(userAgent)) {
    browser = 'Edge'
    browserVersion = userAgent.match(/Edg\/(\d+)/)?.[1] ?? ''
  } else if (userAgent.includes('Chrome')) {
    browser = 'Chrome'
    const match = userAgent.match(/Chrome\/(\d+)/)
    browserVersion = match ? match[1] : ''
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox'
    const match = userAgent.match(/Firefox\/(\d+)/)
    browserVersion = match ? match[1] : ''
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari'
    const match = userAgent.match(/Version\/(\d+)/)
    browserVersion = match ? match[1] : ''
  } else if (userAgent.includes('Edge')) {
    browser = 'Edge'
    const match = userAgent.match(/Edge\/(\d+)/)
    browserVersion = match ? match[1] : ''
  }

  // OS detection
  if (userAgent.includes('iPad')) {
    os = 'iOS'
    deviceType = 'tablet'
  } else if (userAgent.includes('iPhone')) {
    os = 'iOS'
    deviceType = 'mobile'
  } else if (userAgent.includes('Windows')) {
    os = 'Windows'
    if (userAgent.includes('Windows NT 10')) osVersion = '10'
    else if (userAgent.includes('Windows NT 11')) osVersion = '11'
    deviceType = 'desktop'
  } else if (userAgent.includes('Mac OS X')) {
    os = 'macOS'
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/)
    osVersion = match ? match[1].replace('_', '.') : ''
    deviceType = 'desktop'
  } else if (userAgent.includes('iPhone')) {
    os = 'iOS'
    deviceType = 'mobile'
  } else if (userAgent.includes('iPad')) {
    os = 'iOS'
    deviceType = 'tablet'
  } else if (userAgent.includes('Android')) {
    os = 'Android'
    deviceType = userAgent.includes('Mobile') ? 'mobile' : 'tablet'
  } else if (userAgent.includes('Linux')) {
    os = 'Linux'
    deviceType = 'desktop'
  }

  return { browser, browserVersion, os, osVersion, deviceType }
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  const time = recordedTime(dateString)
  if (time === null) return NOT_RECORDED
  const date = new Date(time)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

  return date.toLocaleDateString()
}
